import React, { useState, useEffect } from "react";
import {
	Paper,
	Collapse,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TableContainer,
	IconButton,
	Tooltip,
} from "@mui/material";
import colors from "../../../../assets/theme/light/colors";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { Link } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
	TableCollapseIcon,
	TableExpandIcon,
	TaskIcon,
} from "../../../../components/Icon";
import IconBadge from "../../../../components/StatusCell/IconBadge";

const theme = createTheme({
	components: {
		MuiTooltip: {
			styleOverrides: {
				tooltip: {
					padding: "10px !important",
					borderRadius: "2px !important",
					backgroundColor: "white !important",
					boxShadow: `${colors.box_shadow_color} 1.95px 1.95px 2.6px !important`,
					color: "black",
				},
			},
		},
	},
});

export function TimeByProjectTable({
	t,
	data,
	handleOpenModal,
	timeByContractor,
	generateTaskRoute,
	totalProjectsTime,
}) {
	const [expandAll, setExpandAll] = useState(false);
	const [expanded, setExpanded] = useState(false);
	const [expandedIDs, setExpandedIDs] = useState([]);
	const [expandedTask, setExpandedTask] = useState(false);
	const [expandedTaskIDs, setExpandedTaskIDs] = useState([]);
	const [rows, setRows] = useState([]);

	useEffect(() => {
		function addAnotherIdToProjects(dataArray) {
			dataArray.forEach((row) => {
				row?.employees.forEach((employee) => {
					employee.selectedId = uuidv4();
					employee?.tasks.forEach((task) => {
						task.task = true;
					});
				});
			});
			return dataArray;
		}
		setRows(addAnotherIdToProjects(data));
	}, [data]);

	function handleExpand(id) {
		setExpanded(true);
		if (!expandedIDs.includes(id)) setExpandedIDs([...expandedIDs, id]);
	}
	function handleCollapse(id) {
		const next = expandedIDs.filter((eid) => eid !== id);
		setExpandedIDs(next);
		if (next.length === 0) setExpanded(false);
	}
	function handleExpandTask(id) {
		setExpandedTask(true);
		if (!expandedTaskIDs.includes(id))
			setExpandedTaskIDs([...expandedTaskIDs, id]);
	}
	function handleCollapseTask(id) {
		const next = expandedTaskIDs.filter((tid) => tid !== id);
		setExpandedTaskIDs(next);
		if (next.length === 0) setExpandedTask(false);
	}
	function handleExpandAll() {
		const allProj = rows.map((r) => r.id);
		const allEmp = rows.flatMap((r) => r.employees.map((e) => e.selectedId));
		setExpandAll(true);
		setExpanded(true);
		setExpandedTask(true);
		setExpandedIDs(allProj);
		setExpandedTaskIDs(allEmp);
	}
	function handleCollapseAll() {
		setExpandAll(false);
		setExpanded(false);
		setExpandedIDs([]);
		setExpandedTask(false);
		setExpandedTaskIDs([]);
	}

	function Row({ row, collapsible, parent, parentName }) {
		const { employees } = row;
		return (
			<React.Fragment>
				{row.task ? (
					<TableRow>
						<TableCell sx={{ width: "5%", px: 2 }}>
							{collapsible && (
								<IconButton size="small">
									{expandedIDs.includes(row.id) ? (
										<KeyboardArrowUpIcon
											onClick={() => handleCollapse(row.id)}
										/>
									) : (
										<KeyboardArrowDownIcon
											onClick={() => handleExpand(row.id)}
										/>
									)}
								</IconButton>
							)}
						</TableCell>
						<TableCell
							component={Link}
							to={generateTaskRoute(row, parentName, parent)}
							onClick={(e) => {
								e.preventDefault();
								handleOpenModal(e, row, parentName, parent);
							}}
							className="link-cell task"
							sx={{ width: "40%", px: 2, textDecoration: "none" }}
						>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									height: 30,
									gap: 8,
								}}
							>
								{!collapsible && <TaskIcon />}
								<div
									style={{
										whiteSpace: "nowrap",
										overflow: "hidden",
										textOverflow: "ellipsis",
										width: "100%",
									}}
								>
									{row.name}
								</div>
							</div>
						</TableCell>
						<TableCell align="right" sx={{ width: "15%", px: 2 }}>
							{row.billableHours && row.billableHours !== "00:00"
								? row.billableHours
								: ""}
						</TableCell>
						<TableCell align="right" sx={{ width: "15%", px: 2 }}>
							{row.nonBillableHours && row.nonBillableHours !== "00:00"
								? row.nonBillableHours
								: ""}
						</TableCell>
						<TableCell align="right" sx={{ width: "15%", px: 2 }}>
							{row.totalOfHours}
						</TableCell>
						{!timeByContractor && <TableCell sx={{ width: "15%" }} />}
					</TableRow>
				) : (
					<TableRow>
						<TableCell sx={{ width: "5%", px: 2 }}>
							{collapsible && (
								<IconButton size="small">
									{expandedIDs.includes(row.id) ? (
										<KeyboardArrowUpIcon
											onClick={() => handleCollapse(row.id)}
										/>
									) : (
										<KeyboardArrowDownIcon
											onClick={() => handleExpand(row.id)}
										/>
									)}
								</IconButton>
							)}
						</TableCell>
						<TableCell sx={{ width: "40%", px: 2, fontWeight: 500 }}>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									height: 30,
									gap: 8,
								}}
							>
								{!collapsible && <TaskIcon />}
								<div
									style={{
										whiteSpace: "nowrap",
										overflow: "hidden",
										textOverflow: "ellipsis",
										width: "100%",
									}}
								>
									{row.name}
								</div>
							</div>
						</TableCell>
						<TableCell align="right" sx={{ width: "15%", px: 2 }}>
							{row.billableHours && row.billableHours !== "00:00"
								? row.billableHours
								: ""}
						</TableCell>
						<TableCell align="right" sx={{ width: "15%", px: 2 }}>
							{row.nonBillableHours && row.nonBillableHours !== "00:00"
								? row.nonBillableHours
								: ""}
						</TableCell>
						<TableCell
							align="right"
							sx={{ width: "15%", px: 2, fontWeight: "bold" }}
						>
							{row.totalOfHours}
						</TableCell>
						{!timeByContractor && <TableCell sx={{ width: "15%" }} />}
					</TableRow>
				)}

				{collapsible &&
					expanded &&
					expandedIDs.includes(row.id) &&
					employees.map((emp) => (
						<React.Fragment key={emp.selectedId}>
							<TableRow>
								<TableCell
									sx={{ width: "5%", px: 2, textAlign: "end" }}
								>
									<IconButton size="small">
										{expandedTaskIDs.includes(emp.selectedId) ? (
											<KeyboardArrowUpIcon
												onClick={() =>
													handleCollapseTask(emp.selectedId)
												}
											/>
										) : (
											<KeyboardArrowDownIcon
												onClick={() =>
													handleExpandTask(emp.selectedId)
												}
											/>
										)}
									</IconButton>
								</TableCell>
								<TableCell sx={{ width: "40%", px: 2 }}>
									{emp.name}
								</TableCell>
								<TableCell align="right" sx={{ width: "15%", px: 2 }}>
									{emp.billableHours && emp.billableHours !== "00:00"
										? emp.billableHours
										: ""}
								</TableCell>
								<TableCell align="right" sx={{ width: "15%", px: 2 }}>
									{emp.nonBillableHours &&
									emp.nonBillableHours !== "00:00"
										? emp.nonBillableHours
										: ""}
								</TableCell>
								<TableCell align="right" sx={{ width: "15%", px: 2 }}>
									{emp.totalOfHours}
								</TableCell>
								{!timeByContractor && (
									<TableCell
										align="center"
										sx={{ width: "15%", px: 2 }}
									>
										{emp.percentageOfTotal}%
									</TableCell>
								)}
							</TableRow>
							<TableRow
								sx={{
									display:
										expandedTask &&
										expandedTaskIDs.includes(emp.selectedId)
											? "table-row"
											: "none",
								}}
							>
								<TableCell
									colSpan={!timeByContractor ? 6 : 5}
									sx={{ p: 0, border: "none" }}
								>
									<Collapse
										in={expandedTask}
										timeout="auto"
										unmountOnExit
									>
										<Table size="small" sx={{ tableLayout: "fixed" }}>
											<TableBody>
												{emp.tasks.map((task) => (
													<Row
														key={task.id}
														row={task}
														collapsible={false}
														parent={emp.id}
														parentName={emp.name}
													/>
												))}
											</TableBody>
										</Table>
									</Collapse>
								</TableCell>
							</TableRow>
						</React.Fragment>
					))}
			</React.Fragment>
		);
	}

	return rows.length > 0 ? (
		<TableContainer
			component={Paper}
			className="table-timesheet-height"
			sx={{ width: "100%" }}
		>
			<Table
				size="small"
				sx={{
					width: "100%",
					tableLayout: "fixed",
				}}
			>
				<TableHead>
					<TableRow>
						<TableCell
							sx={{
								width: "5%",
								px: 2,
								position: "sticky",
								top: 0,
								zIndex: 2,
								backgroundColor: "#fff",
							}}
						>
							<ThemeProvider theme={theme}>
								<Tooltip
									title={
										expandAll
											? t("table.CollapseAll")
											: t("table.ExpandAll")
									}
								>
									<IconButton
										size="small"
										onClick={
											expandAll ? handleCollapseAll : handleExpandAll
										}
									>
										{expandAll ? (
											<TableCollapseIcon />
										) : (
											<TableExpandIcon />
										)}
									</IconButton>
								</Tooltip>
							</ThemeProvider>
						</TableCell>
						<TableCell
							sx={{
								width: "40%",
								px: 2,
								position: "sticky",
								top: 0,
								zIndex: 2,
								backgroundColor: "#fff",
							}}
						>
							{t("timeByEmployee.name")}
						</TableCell>
						<TableCell
							align="right"
							sx={{
								width: "15%",
								px: 2,
								position: "sticky",
								top: 0,
								zIndex: 2,
								backgroundColor: "#fff",
							}}
						>
							{t("timeByEmployee.billable")}
						</TableCell>
						<TableCell
							align="right"
							sx={{
								width: "15%",
								px: 2,
								position: "sticky",
								top: 0,
								zIndex: 2,
								backgroundColor: "#fff",
							}}
						>
							{t("timeByEmployee.nonBillable")}
						</TableCell>
						<TableCell
							align="right"
							sx={{
								width: "15%",
								px: 2,
								position: "sticky",
								top: 0,
								zIndex: 2,
								backgroundColor: "#fff",
							}}
						>
							{t("timeByEmployee.total")}
						</TableCell>
						{!timeByContractor && (
							<TableCell
								align="center"
								sx={{
									width: "15%",
									px: 2,
									position: "sticky",
									top: 0,
									zIndex: 2,
									backgroundColor: "#fff",
								}}
							>
								%
							</TableCell>
						)}
					</TableRow>
				</TableHead>
				<TableBody>
					{rows.map((row) => (
						<Row key={row.id} row={row} collapsible={true} />
					))}
					{timeByContractor && (
						<TableRow>
							<TableCell />
							<TableCell sx={{ px: 2, fontWeight: "bold" }}>
								{t("timeByEmployee.total")}
							</TableCell>
							<TableCell />
							<TableCell />
							<TableCell
								align="right"
								sx={{ px: 2, fontWeight: "bold" }}
							>
								{totalProjectsTime}
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
		</TableContainer>
	) : null;
}
